import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { WsspAuthService } from '../service/microservices/wsspApi';
import { useNotification } from './NotificationContext';

const WsspAuthContext = createContext(null);

export const useWsspAuth = () => {
  const context = useContext(WsspAuthContext);
  if (!context) {
    throw new Error('useWsspAuth debe ser usado dentro de WsspAuthProvider');
  }
  return context;
};

export const WsspAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [wsspToken, setWsspToken] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [isOTPRequired, setIsOTPRequired] = useState(false);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const { showNotification } = useNotification();

  // Verificar sesión existente al montar
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const storedToken = WsspAuthService.getStoredToken();
        const storedPhone = localStorage.getItem('wssp_phone');

        if (storedToken && storedPhone) {
          const validation = await WsspAuthService.validateToken(storedToken);
          
          if (validation.success) {
            setWsspToken(storedToken);
            setPhoneNumber(storedPhone);
            setIsAuthenticated(true);
          } else {
            // Token inválido, limpiar
            localStorage.removeItem('wssp_token');
            localStorage.removeItem('wssp_phone');
            setIsOTPRequired(true);
          }
        } else {
          setIsOTPRequired(true);
        }
      } catch (error) {
        setIsOTPRequired(true);
      } finally {
        setLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  // Escuchar eventos de renovación de token y logout del sistema principal
  useEffect(() => {
    const handleTokenRenewal = (event) => {
      const { token } = event.detail;
      setWsspToken(token);
    };

    const handleAuthRequired = () => {
      setIsAuthenticated(false);
      setWsspToken(null);
      setPhoneNumber('');
      setIsOTPRequired(true);
      showNotification({
        message: 'Sesión expirada. Por favor, inicia sesión nuevamente.',
        severity: 'warning',
      });
    };

    const handleMainLogout = () => {
      // Cuando el usuario cierra sesión en el sistema principal
      // limpiar también la sesión de WSSP
      setIsAuthenticated(false);
      setWsspToken(null);
      setPhoneNumber('');
      setIsOTPRequired(true);
      setIsOTPSent(false);
      setIsVerifying(false);
      setOtpLoading(false);
      
      // Limpiar localStorage por si acaso
      localStorage.removeItem('wssp_token');
      localStorage.removeItem('wssp_phone');
    };

    window.addEventListener('wssp:token-renewed', handleTokenRenewal);
    window.addEventListener('wssp:auth-required', handleAuthRequired);
    window.addEventListener('auth:logout', handleMainLogout);

    return () => {
      window.removeEventListener('wssp:token-renewed', handleTokenRenewal);
      window.removeEventListener('wssp:auth-required', handleAuthRequired);
      window.removeEventListener('auth:logout', handleMainLogout);
    };
  }, [showNotification]);

  // Iniciar flujo OTP
  const initiateOTPFlow = useCallback(async (phone) => {
    setOtpLoading(true);
    try {
      const response = await WsspAuthService.requestOTP(phone);

      if (response.success) {
        setPhoneNumber(phone);
        setIsOTPSent(true);
        showNotification({
          message: 'Código OTP enviado a tu WhatsApp',
          severity: 'success',
        });
        return { success: true };
      } else {
        showNotification({
          message: response.message || 'Error enviando código OTP',
          severity: 'error',
        });
        return { success: false, error: response.message };
      }
    } catch (error) {
      showNotification({
        message: error.response?.data?.message || 'Error enviando código OTP',
        severity: 'error',
      });
      return { success: false, error: error.message };
    } finally {
      setOtpLoading(false);
    }
  }, [showNotification]);

  // Verificar código OTP
  const verifyOTP = useCallback(async (otp) => {
    setIsVerifying(true);
    try {
      const response = await WsspAuthService.verifyOTP(phoneNumber, otp);

      if (response.success && response.token) {
        setWsspToken(response.token);
        setIsAuthenticated(true);
        setIsOTPRequired(false);
        setIsOTPSent(false);
        
        showNotification({
          message: '¡Autenticación exitosa!',
          severity: 'success',
        });
        
        return { success: true };
      } else {
        showNotification({
          message: response.message || 'Código OTP inválido',
          severity: 'error',
        });
        return { success: false, error: response.message };
      }
    } catch (error) {
      showNotification({
        message: error.response?.data?.message || 'Error verificando código OTP',
        severity: 'error',
      });
      return { success: false, error: error.message };
    } finally {
      setIsVerifying(false);
    }
  }, [phoneNumber, showNotification]);

  // Cerrar sesión
  const logout = useCallback(async () => {
    try {
      if (wsspToken) {
        await WsspAuthService.logout(wsspToken);
      }
      
      setIsAuthenticated(false);
      setWsspToken(null);
      setPhoneNumber('');
      setIsOTPRequired(true);
      
      showNotification({
        message: 'Sesión cerrada correctamente',
        severity: 'info',
      });
    } catch (error) {
      // Aún así limpiar el estado local
      setIsAuthenticated(false);
      setWsspToken(null);
      setPhoneNumber('');
      setIsOTPRequired(true);
      showNotification({
        message: 'Error al cerrar sesión',
        severity: 'error',
      });
    }
  }, [wsspToken, showNotification]);

  // Resetear flujo OTP
  const resetOTPFlow = useCallback(() => {
    setIsOTPSent(false);
    setPhoneNumber('');
    setIsVerifying(false);
    setOtpLoading(false);
  }, []);

  const value = {
    // Estado
    isAuthenticated,
    wsspToken,
    phoneNumber,
    loading,
    isOTPRequired,
    isOTPSent,
    isVerifying,
    otpLoading,
    
    // Métodos
    initiateOTPFlow,
    verifyOTP,
    logout,
    resetOTPFlow,
    setIsOTPRequired,
  };

  return (
    <WsspAuthContext.Provider value={value}>
      {children}
    </WsspAuthContext.Provider>
  );
};

export default WsspAuthProvider;
